// Slideshow hero (sections/slideshow-hero.liquid); <mv-bg-video> is shared with the parallax
// hero (sections/parallax-hero.liquid, snippets/hero-background.liquid).
//
// <mv-hero-slider> wraps a `.splide` root (one slide per page, no arrows) and mounts Splide
// (assets/splide.min.js, deferred from theme.liquid) from data-* attributes (all strings):
//   data-type             loop | fade   (fade rewinds; loop clones the ends)
//   data-drag             "true": the slides can be dragged / swiped
//   data-autoplay         "true" + data-autoplay-speed seconds between moves (pauses on hover/focus)
//   data-pagination       "true": the section's own .splide__pagination is filled with dots
// Background videos on slides that leave the view are paused and the active slide's resumed.
// In the theme editor, selecting a slide block scrolls the slider to it.
//
// <mv-bg-video> is a muted, looping background video: its <source data-src> URLs are attached
// only once the element comes within 200px of the viewport, playback pauses while it is
// off-screen or its slide is inactive, and the optional [data-mv-sound] button unmutes it.
(() => {
  const define = (window.Multiverse && Multiverse.defineOnce) || ((name, cls) => {
    if (!customElements.get(name)) customElements.define(name, cls);
  });
  const flag = (value) => value === 'true';

  // ---- mv-bg-video ----
  class BgVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      if (!this.video) return;
      this.soundButton = this.querySelector('[data-mv-sound]');
      this.inView = false;
      this.slideActive = true;

      if (!this._bound) {
        this._bound = true;
        if (this.soundButton) this.soundButton.addEventListener('click', () => this.toggleSound());
      }
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.inView = entry.isIntersecting;
          if (this.inView) this.load();
          this.sync();
        });
      }, { rootMargin: '200px 0px' });
      this.observer.observe(this);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    load() {
      if (this.loaded) return;
      this.loaded = true;
      this.querySelectorAll('source[data-src]').forEach((source) => {
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
      });
      this.video.load();
    }

    // Called by the slider: false pauses the video while its slide is out of the track's view.
    setActive(active) {
      this.slideActive = active;
      this.sync();
    }

    sync() {
      if (!this.loaded) return;
      const shouldPlay = this.inView && this.slideActive && this.isConnected;
      if (shouldPlay) {
        const promise = this.video.play();
        if (promise && promise.catch) promise.catch(() => {});
        this.classList.add('is-playing');
      } else {
        this.video.pause();
        this.classList.remove('is-playing');
      }
    }

    toggleSound() {
      this.load();
      const unmuted = this.video.muted;
      this.video.muted = !unmuted;
      if (this.soundButton) {
        this.soundButton.setAttribute('aria-pressed', unmuted ? 'true' : 'false');
        this.soundButton.setAttribute('aria-label', unmuted ? 'Mute video' : 'Unmute video');
      }
      this.sync();
    }
  }

  // ---- mv-hero-slider ----
  class HeroSlider extends HTMLElement {
    connectedCallback() {
      this.onBlockSelect = this.onBlockSelect || ((event) => {
        if (!this.splide || !this.contains(event.target)) return;
        const slide = event.target.closest('.splide__slide');
        const index = slide ? [...slide.parentNode.children].indexOf(slide) : -1;
        if (index >= 0) this.splide.go(index);
      });
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      this.mount();
    }

    disconnectedCallback() {
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.destroy();
    }

    mount() {
      this.destroy();
      const root = this.querySelector('.splide');
      if (!root) return;

      if (typeof window.Splide === 'undefined') {
        // splide.min.js is deferred before this file; wait for it rather than giving up.
        const loading = document.readyState === 'loading';
        if (loading) {
          document.addEventListener('DOMContentLoaded', () => this.isConnected && this.mount(), { once: true });
        } else if (!this._retryTimer) {
          this._retryTimer = window.setTimeout(() => {
            this._retryTimer = null;
            if (this.isConnected) this.mount();
          }, 50);
        }
        return;
      }

      const d = this.dataset;
      const type = d.type === 'fade' ? 'fade' : 'loop';
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.splide = new window.Splide(root, {
        type,
        rewind: type === 'fade',
        perPage: 1,
        perMove: 1,
        gap: 0,
        arrows: false,
        pagination: flag(d.pagination),
        drag: flag(d.drag),
        autoplay: flag(d.autoplay) && !reducedMotion,
        interval: (parseFloat(d.autoplaySpeed) || 10) * 1000,
        pauseOnHover: true,
        pauseOnFocus: true,
        speed: type === 'fade' ? 800 : 600,
        i18n: { prev: 'Previous slide', next: 'Next slide', slideX: 'Go to slide %s' },
      });
      this.splide.on('mounted moved', () => this.syncVideos());
      this.splide.mount();
    }

    syncVideos() {
      if (this.splide === null) return;
      this.querySelectorAll('.splide__slide').forEach((slide) => {
        const shown = slide.classList.contains('is-active') || slide.classList.contains('is-visible');
        slide.querySelectorAll('mv-bg-video').forEach((video) => {
          if (typeof video.setActive === 'function') video.setActive(shown);
        });
      });
    }

    destroy() {
      if (this.splide) {
        this.splide.destroy();
        this.splide = null;
      }
      if (this._retryTimer) {
        window.clearTimeout(this._retryTimer);
        this._retryTimer = null;
      }
    }
  }

  define('mv-bg-video', BgVideo);
  define('mv-hero-slider', HeroSlider);
})();
